import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/lib/AuthContext";
import Footer from "@/components/Footer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { commonStyles } from "@/lib/theme";
import { cn } from "@/lib/utils";
import RotatingText from './RotatingText';
import { LanguageSwitcher } from './LanguageSwitcher';

const Landing = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, signUp, demoSignIn, resetPassword } = useAuth();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      toast({
        title: t('auth.messages.consentRequired'),
        description: t('auth.messages.consentRequired'),
        variant: "destructive",
      });
      return;
    }
    setConsentError(false);
    try {
      if (isSignUp) {
        await signUp(email, password);
        toast({
          title: t('auth.messages.checkEmail'),
          description: t('auth.messages.confirmationSent'),
        });
      } else {
        await signIn(email, password);
        navigate("/dashboard");
      }
    } catch (error) {
      toast({
        title: t('auth.messages.authError'),
        description: error instanceof Error ? error.message : t('common.error'),
        variant: "destructive",
      });
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast({
        title: t('auth.messages.emailRequired'),
        description: t('auth.messages.emailRequired'),
        variant: "destructive",
      });
      return;
    }
    
    try {
      await resetPassword(resetEmail);
      toast({
        title: t('auth.messages.checkEmail'),
        description: t('auth.messages.resetSent'),
      });
      setShowForgotPassword(false);
    } catch (error) {
      toast({
        title: t('auth.messages.resetError'),
        description: error instanceof Error ? error.message : t('common.error'),
        variant: "destructive",
      });
    }
  };

  const handleDemoAccess = async () => {
    try {
      await demoSignIn();
      navigate("/demo");
    } catch (error) {
      toast({
        title: t('auth.messages.demoError'),
        description: t('auth.messages.demoErrorDesc'),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#2A2B2A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center p-4 md:p-6 bg-[#333433]/50 backdrop-blur-sm border-b border-[#404140]">
        <img src="/JobPin_logo_2edt_white.svg" alt="JobPin" className="h-10 md:h-12" />
        <div className="flex gap-4 items-center">
          <LanguageSwitcher />
          <Button 
            variant="ghost" 
            onClick={() => {
              setIsSignUp(false);
              setAuthDialogOpen(true);
            }}
            className={commonStyles.button.ghostPrimary}
          >
            {t('navigation.login')}
          </Button>
          <Button 
            onClick={() => {
              setIsSignUp(true);
              setAuthDialogOpen(true);
            }}
            className={commonStyles.button.primary}
          >
            {t('navigation.signup')}
          </Button>
        </div>
      </nav>

      {/* Beta Banner */}
      <div className="fixed top-[72px] md:top-[88px] left-0 right-0 z-40 bg-[#CCF751]/95 text-[#2C2E2D] flex items-center justify-center h-14 text-center font-medium">
        <a 
          href="mailto:hi@stefanmp.com?subject=JobPin%20feedback" 
          className="py-3.5 cursor-pointer hover:underline transition-all duration-200 hover:bg-[#CCF751] hover:bg-opacity-100 px-4 rounded"
          title="Send us feedback"
        >
          {t('landing.betaBanner')}
        </a>
      </div>

      {/* Hero Section */}
      <section className="pt-40 md:pt-48 lg:pt-52 px-4 py-12 md:py-20 relative min-h-[560px] md:min-h-[640px] lg:min-h-[720px]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-start md:items-center">
          {/* Left: Headlines and CTA */}
          <div className="order-2 md:order-1">
            <div style={{ position: 'relative', height: '160px', marginBottom: '2rem' }} className="flex flex-col items-start text-left">
              <RotatingText
                texts={t('landing.hero.rotatingTexts', { returnObjects: true }) as string[]}
                mainClassName="w-full px-0 py-2 sm:py-3 md:py-4 flex justify-start items-start text-left"
                staggerFrom="first"
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "-100%", opacity: 0 }}
                staggerDuration={0.03}
                splitBy="words"
                splitLevelClassName="overflow-visible pb-3 text-left"
                elementLevelClassName="mx-1 font-extrabold text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight hover:text-[#CCF751] transition-colors duration-300"
                transition={{ type: "spring", damping: 28, stiffness: 280 }}
                rotationInterval={4000}
              />
              <div className="h-[4px] w-28 bg-[#CCF751] rounded-full mt-4"></div>
            </div>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 max-w-xl">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={handleDemoAccess}
                className="bg-[#D9D9D9] text-[#2C2E2D] hover:bg-[#D9D9D9]/90 border-[#D9D9D9] min-w-[140px]"
              >
                {t('landing.hero.tryDemo')}
              </Button>
              <Button 
                onClick={() => {
                  setIsSignUp(true);
                  setAuthDialogOpen(true);
                }}
                className={cn(commonStyles.button.primary, "min-w-[140px]")}
              >
                {t('landing.hero.getStarted')}
              </Button>
            </div>
            <p className="mt-4 text-sm text-gray-400 hidden md:block">
              {t('landing.hero.videoDescription')}
            </p>
          </div>

          {/* Right: Video */}
          <div className="order-1 md:order-2">
            <div className="rounded-2xl shadow-2xl border border-gray-700/60 overflow-hidden w-full max-w-3xl ml-auto">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full h-full object-cover"
              >
                <source src="/jobpin-dashboard-demo.mp4" type="video/mp4" />
                <img
                  src="/dashboard-preview.png"
                  alt="JobPin Dashboard Preview"
                  className="w-full"
                />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Main Argument Section */}
      <section className="py-20 bg-[#333433]/50 backdrop-blur-sm">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 md:gap-16 max-w-6xl mx-auto px-4 py-16">
          {/* T-Shirt Mockup - Left Side */}
          <div className="flex-1 order-2 md:order-1">
            <div className="relative group">
              <img
                src="/jobpin-teeMockup.png"
                alt="JobPin T-Shirt Mockup"
                className="rounded-lg shadow-xl w-full border border-gray-700/50 transition-all duration-500 hover:border-[#CCF751]/50 hover:shadow-2xl hover:shadow-[#CCF751]/10"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2A2B2A]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-end justify-center">
                <span className="text-[#CCF751] font-medium pb-4 text-sm md:text-base">{t('landing.community.merchandise')}</span>
              </div>
            </div>
          </div>
          
          {/* Content - Right Side */}
          <div className="flex-1 md:order-2 order-1">
            <h2 className="text-3xl font-serif font-bold mb-4 text-gray-100">{t('landing.community.title')}</h2>
            <p className="text-lg text-gray-400 mb-6">
              {t('landing.community.description')}
            </p>
            <div className="space-y-4">
              {(t('landing.community.features', { returnObjects: true }) as string[]).map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-[#CCF751] rounded-full"></div>
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Three Arguments Section */}
      <section className="py-20">
        <div className="w-full max-w-7xl mx-auto py-16">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#333433]/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-[#404140]">
              <h3 className="text-xl font-serif font-semibold text-gray-100 mb-3">{t('landing.features.centralized.title')}</h3>
              <p className="text-gray-400">
                {t('landing.features.centralized.description')}
              </p>
            </div>

            <div className="bg-[#333433]/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-[#404140]">
              <h3 className="text-xl font-serif font-semibold text-gray-100 mb-3">{t('landing.features.progress.title')}</h3>
              <p className="text-gray-400">
                {t('landing.features.progress.description')}
              </p>
            </div>

            <div className="bg-[#333433]/50 backdrop-blur-sm rounded-lg p-6 shadow-sm border border-[#404140]">
              <h3 className="text-xl font-serif font-semibold text-gray-100 mb-3">{t('landing.features.quick.title')}</h3>
              <p className="text-gray-400">
                {t('landing.features.quick.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="text-center py-20 bg-[#333433]/50 backdrop-blur-sm">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-100 mb-6">
          {t('landing.cta.title')}
        </h2>
        <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
          {t('landing.cta.description')}
        </p>
        <Button 
          size="lg" 
          onClick={() => {
            setIsSignUp(true);
            setAuthDialogOpen(true);
          }}
          className={commonStyles.button.primary}
        >
          {t('landing.cta.button')}
        </Button>
      </section>

      {/* Footer */}
      <Footer />

      {/* Auth Dialog */}
      <Dialog open={authDialogOpen} onOpenChange={setAuthDialogOpen}>
        <DialogContent className="sm:max-w-md bg-[#333433] border-[#404140] text-white">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              {showForgotPassword 
                ? t('auth.reset.title')
                : isSignUp 
                  ? t('auth.signup.title')
                  : t('auth.login.title')}
            </DialogTitle>
            <DialogDescription className="text-center text-[#D9D9D9]/70">
              {showForgotPassword 
                ? t('auth.reset.description')
                : isSignUp 
                  ? t('auth.signup.description')
                  : t('auth.login.description')}
            </DialogDescription>
          </DialogHeader>
          {showForgotPassword ? (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-[#D9D9D9]">{t('auth.reset.email')}</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#2C2E2D] border-[#404140] text-[#D9D9D9] placeholder:text-[#818881]"
                />
              </div>
              <Button 
                type="submit" 
                className={cn(commonStyles.button.primary, "w-full")}
              >
                {t('auth.reset.button')}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-[#CCF751] hover:text-[#CCF751]/80 text-sm"
                >
                  {t('auth.reset.backToLogin')}
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#D9D9D9]">{t('auth.login.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-[#2C2E2D] border-[#404140] text-[#D9D9D9] placeholder:text-[#818881]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#D9D9D9]">{t('auth.login.password')}</Label>
                <div className="relative">
              <Input
                id="password"
                    type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-[#2C2E2D] border-[#404140] text-[#D9D9D9] pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#D9D9D9]/70 hover:text-[#D9D9D9] focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
            </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="terms" 
                  checked={consent} 
                  onCheckedChange={(checked) => {
                    setConsent(checked === true ? true : false);
                    if (checked) setConsentError(false);
                  }} 
                  className={cn(
                    "border-[#404140] data-[state=checked]:bg-[#CCF751] data-[state=checked]:text-[#2C2E2D]",
                    consentError && "border-red-500 border-2"
                  )}
                />
                <label
                  htmlFor="terms"
                  className={cn(
                    "text-sm text-[#D9D9D9]/70",
                    consentError && "text-red-400"
                  )}
                >
                  {t('auth.consent.text')}{" "}
                  <Link to="/terms" className="text-[#CCF751] hover:text-[#CCF751]/80">
                    {t('auth.consent.terms')}
                  </Link>{" "}
                  {t('auth.consent.and')}{" "}
                  <Link to="/privacy" className="text-[#CCF751] hover:text-[#CCF751]/80">
                    {t('auth.consent.privacy')}
                  </Link>
                </label>
              </div>
              {consentError && (
                <div className="text-red-400 text-xs mt-1">
                  {t('auth.consent.error')}
                </div>
              )}
              {!isSignUp && (
                <div className="text-center">
                  <button 
                    type="button" 
                    onClick={() => setShowForgotPassword(true)}
                    className="text-[#CCF751] hover:text-[#CCF751]/80 text-sm"
                  >
                    {t('auth.login.forgotPassword')}
                  </button>
            </div>
              )}
              <Button 
                type="submit" 
                className={cn(commonStyles.button.primary, "w-full")}
              >
                {isSignUp ? t('auth.signup.button') : t('auth.login.button')}
            </Button>
              <div className="text-center">
            <button
              type="button"
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-[#CCF751] hover:text-[#CCF751]/80 text-sm"
                >
                  {isSignUp ? t('auth.signup.hasAccount') : t('auth.login.noAccount')}
            </button>
          </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing; 